// RFC 6238 TOTP. Uses Web Crypto (SubtleCrypto) — available in popup, SW and content scripts.
// We support SHA-1 / SHA-256 / SHA-512 and 6/8-digit codes.

const BASE32_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";

export function base32Decode(input: string): Uint8Array {
  const clean = input.toUpperCase().replace(/=+$/g, "").replace(/[^A-Z2-7]/g, "");
  const out: number[] = [];
  let bits = 0;
  let acc = 0;
  for (const ch of clean) {
    const idx = BASE32_ALPHABET.indexOf(ch);
    if (idx < 0) continue;
    acc = (acc << 5) | idx;
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      out.push((acc >>> bits) & 0xff);
    }
  }
  return new Uint8Array(out);
}

export interface TotpParams {
  digits: number; // 6 or 8
  period: number; // seconds (default 30)
  algorithm: "SHA-1" | "SHA-256" | "SHA-512";
}

const DEFAULT_PARAMS: TotpParams = { digits: 6, period: 30, algorithm: "SHA-1" };

export interface ParsedOtpAuth {
  secretBase32: string;
  digits?: number;
  period?: number;
  algorithm?: string;
  label?: string;
  issuer?: string;
}

export function parseOtpAuthUri(uri: string): ParsedOtpAuth | null {
  if (!uri.startsWith("otpauth://")) return null;
  try {
    const u = new URL(uri);
    if (u.host.toLowerCase() !== "totp") return null;
    const secret = u.searchParams.get("secret");
    if (!secret) return null;
    return {
      secretBase32: secret,
      digits: u.searchParams.has("digits")
        ? parseInt(u.searchParams.get("digits")!, 10)
        : undefined,
      period: u.searchParams.has("period")
        ? parseInt(u.searchParams.get("period")!, 10)
        : undefined,
      algorithm: u.searchParams.get("algorithm") ?? undefined,
      label: decodeURIComponent(u.pathname.slice(1)),
      issuer: u.searchParams.get("issuer") ?? undefined,
    };
  } catch {
    return null;
  }
}

/** Extracts only the secret. If user paste otpauth://, we keep just the secret;
 * non-default params (algorithm/digits/period) are lost. Good enough for ~99% of services. */
export function normalizeSecret(input: string): string {
  const trimmed = input.trim();
  const parsed = parseOtpAuthUri(trimmed);
  if (parsed) return parsed.secretBase32;
  // Strip spaces commonly added by providers (e.g. "ABCD EFGH IJKL")
  return trimmed.replace(/\s+/g, "").toUpperCase();
}

export function isValidSecret(input: string): boolean {
  const s = normalizeSecret(input);
  if (s.length === 0) return false;
  return /^[A-Z2-7]+=*$/.test(s) && base32Decode(s).length > 0;
}

export interface TotpResult {
  code: string;
  secondsRemaining: number;
  period: number;
}

export async function generateTotp(
  secretBase32: string,
  params: Partial<TotpParams> = {},
): Promise<TotpResult> {
  const p: TotpParams = { ...DEFAULT_PARAMS, ...params };
  const keyBytes = base32Decode(normalizeSecret(secretBase32));
  if (keyBytes.length === 0) throw new Error("invalid TOTP secret");

  const now = Math.floor(Date.now() / 1000);
  const counter = Math.floor(now / p.period);

  const counterBuf = new ArrayBuffer(8);
  const dv = new DataView(counterBuf);
  // Counter is 64-bit big-endian. JS bitwise ops are 32-bit only.
  dv.setUint32(0, Math.floor(counter / 0x100000000));
  dv.setUint32(4, counter >>> 0);

  const key = await crypto.subtle.importKey(
    "raw",
    // copy into fresh ArrayBuffer for typings
    new Uint8Array(keyBytes).buffer,
    { name: "HMAC", hash: p.algorithm },
    false,
    ["sign"],
  );
  const sigBuf = await crypto.subtle.sign("HMAC", key, counterBuf);
  const sig = new Uint8Array(sigBuf);
  const offset = sig[sig.length - 1] & 0x0f;
  const binCode =
    ((sig[offset] & 0x7f) << 24) |
    (sig[offset + 1] << 16) |
    (sig[offset + 2] << 8) |
    sig[offset + 3];

  const code = (binCode % 10 ** p.digits).toString().padStart(p.digits, "0");
  const secondsRemaining = p.period - (now % p.period);
  return { code, secondsRemaining, period: p.period };
}
